import { useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  ANONYMOUS_IDENTITY_CLAIMED_EVENT,
  ANONYMOUS_ID_STORAGE_KEY,
  adoptAnonymousIdFromStorage,
  clearAnonymousIdentityResolutionFailure,
  resolveParentManagedAnonymousIdentityOrigin,
  getAnonymousId,
  isParentManagedAnonymousIdentity,
  rememberAnonymousId,
  reportAnonymousIdentityResolutionFailure,
  subscribeAnonymousIdentity,
} from '@/lib/auth/anonymousIdentity';
import { reportError } from '@/lib/telemetry';
import {
  getGuestCredential,
  isGuestCredential,
  rememberGuestCredential,
} from '@/lib/auth/guestCredential';

const UUID_V4_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const PARENT_IDENTITY_TIMEOUT_MS = 1500;

function removeRetiredAnonymousIdentityHandoffParam(): void {
  const url = new URL(globalThis.window.location.href);
  if (!url.searchParams.has('anonymous_identity_handoff')) return;
  url.searchParams.delete('anonymous_identity_handoff');
  globalThis.window.history.replaceState(globalThis.window.history.state, '', url.toString());
}

function replaceParentAnonymousIdentity(
  expectedAnonymousId: string,
  replacementAnonymousId: string,
  parentOrigin: string
): Promise<void> {
  const parentWindow = globalThis.window.parent;
  const requestId = uuidv4();
  return new Promise((resolve, reject) => {
    let settled = false;
    const cleanup = () => {
      globalThis.window.clearTimeout(timeoutId);
      globalThis.window.removeEventListener('message', handleReplacementResult);
    };
    const finish = (error?: Error) => {
      if (settled) return;
      settled = true;
      cleanup();
      if (error) reject(error);
      else resolve();
    };
    const handleReplacementResult = (event: MessageEvent) => {
      if (event.source !== parentWindow || event.origin !== parentOrigin) return;
      if (
        !event.data ||
        event.data.source !== 'atrivial-embed-script' ||
        event.data.type !== 'atrivial-anonymous-identity-replace-result' ||
        event.data.requestId !== requestId ||
        typeof event.data.anonymousId !== 'string' ||
        !UUID_V4_PATTERN.test(event.data.anonymousId)
      ) {
        return;
      }
      if (
        (event.data.outcome === 'replaced' || event.data.outcome === 'already_replaced') &&
        event.data.anonymousId === replacementAnonymousId
      ) {
        if (isGuestCredential(event.data.guestCredential)) {
          rememberGuestCredential(replacementAnonymousId, event.data.guestCredential);
        }
        finish();
        return;
      }
      if (event.data.outcome === 'conflict') {
        finish(new Error('Partner identity changed before replacement settled'));
      }
    };

    globalThis.window.addEventListener('message', handleReplacementResult);
    const timeoutId = globalThis.window.setTimeout(
      () => finish(new Error('Partner identity replacement was not acknowledged')),
      PARENT_IDENTITY_TIMEOUT_MS
    );
    parentWindow.postMessage(
      {
        source: 'atrivial-quiz-player',
        type: 'atrivial-anonymous-identity-replace',
        requestId,
        expectedAnonymousId,
        replacementAnonymousId,
        replacementCredential: getGuestCredential(replacementAnonymousId),
      },
      parentOrigin
    );
  });
}

interface ActiveParentIdentityBridge {
  parentWindow: Window;
  parentOrigin: string;
  readonly resolvedId: string | null;
  consumers: Set<(anonymousId: string) => void>;
  cleanup(): void;
}

let activeParentIdentityBridge: ActiveParentIdentityBridge | null = null;

function acquireParentIdentityBridge(
  candidateId: string,
  parentOrigin: string,
  onResolved: (anonymousId: string) => void,
  initialResolvedId: string | null = null
): () => void {
  const parentWindow = globalThis.window.parent;
  if (
    activeParentIdentityBridge?.parentWindow === parentWindow &&
    activeParentIdentityBridge.parentOrigin === parentOrigin
  ) {
    const sharedBridge = activeParentIdentityBridge;
    sharedBridge.consumers.add(onResolved);
    if (sharedBridge.resolvedId) onResolved(sharedBridge.resolvedId);
    return () => releaseParentIdentityBridge(sharedBridge, onResolved);
  }

  let awaitingParentIdentity = false;
  let resolvedId: string | null = null;
  let timeoutId: number | undefined;
  const consumers = new Set([onResolved]);
  const publishParentIdentity = (nextId: string) => {
    globalThis.window.clearTimeout(timeoutId);
    rememberAnonymousId(nextId);
    resolvedId = nextId;
    for (const consumer of consumers) consumer(nextId);
  };
  const acceptParentIdentity = (nextId: string) => {
    if (!awaitingParentIdentity) return;
    awaitingParentIdentity = false;
    publishParentIdentity(nextId);
  };
  const waitForParentIdentity = (fallbackId: string) => {
    awaitingParentIdentity = true;
    resolvedId = null;
    globalThis.window.clearTimeout(timeoutId);
    timeoutId = globalThis.window.setTimeout(
      () => acceptParentIdentity(fallbackId),
      PARENT_IDENTITY_TIMEOUT_MS
    );
  };
  const handleParentIdentity = (event: MessageEvent) => {
    if (event.source !== parentWindow || event.origin !== parentOrigin) return;
    if (
      !event.data ||
      event.data.source !== 'atrivial-embed-script' ||
      event.data.type !== 'atrivial-anonymous-identity' ||
      typeof event.data.anonymousId !== 'string' ||
      !UUID_V4_PATTERN.test(event.data.anonymousId)
    ) {
      return;
    }
    if (awaitingParentIdentity && isGuestCredential(event.data.guestCredential)) {
      rememberGuestCredential(event.data.anonymousId, event.data.guestCredential);
    }
    acceptParentIdentity(event.data.anonymousId);
  };
  const handleClaimedIdentity = (event: Event) => {
    const detail = (
      event as CustomEvent<{ anonymousId?: unknown; replacementAnonymousId?: unknown }>
    ).detail;
    const claimedId = detail?.anonymousId;
    if (typeof claimedId !== 'string' || !UUID_V4_PATTERN.test(claimedId)) return;

    const replacementCandidateId =
      typeof detail.replacementAnonymousId === 'string' &&
      UUID_V4_PATTERN.test(detail.replacementAnonymousId)
        ? detail.replacementAnonymousId
        : uuidv4();
    awaitingParentIdentity = false;
    resolvedId = null;
    void replaceParentAnonymousIdentity(claimedId, replacementCandidateId, parentOrigin)
      .then(() => {
        publishParentIdentity(replacementCandidateId);
        clearAnonymousIdentityResolutionFailure();
      })
      .catch((error) => {
        reportAnonymousIdentityResolutionFailure('claim_settlement_retryable');
        reportError({
          error,
          source: 'AnonymousIdentityClaim.rotation',
          context: { stage: 'parent_replacement_ack' },
        });
      });
  };

  const bridge: ActiveParentIdentityBridge = {
    parentWindow,
    parentOrigin,
    get resolvedId() {
      return resolvedId;
    },
    consumers,
    cleanup() {
      globalThis.window.clearTimeout(timeoutId);
      globalThis.window.removeEventListener('message', handleParentIdentity);
      globalThis.window.removeEventListener(
        ANONYMOUS_IDENTITY_CLAIMED_EVENT,
        handleClaimedIdentity
      );
    },
  };
  activeParentIdentityBridge = bridge;
  globalThis.window.addEventListener('message', handleParentIdentity);
  globalThis.window.addEventListener(ANONYMOUS_IDENTITY_CLAIMED_EVENT, handleClaimedIdentity);
  if (initialResolvedId) {
    publishParentIdentity(initialResolvedId);
  } else {
    waitForParentIdentity(candidateId);
    parentWindow.postMessage(
      {
        source: 'atrivial-quiz-player',
        type: 'atrivial-anonymous-identity-request',
        candidateId,
        candidateCredential: getGuestCredential(candidateId),
      },
      parentOrigin
    );
  }

  return () => releaseParentIdentityBridge(bridge, onResolved);
}

function releaseParentIdentityBridge(
  bridge: ActiveParentIdentityBridge,
  consumer: (anonymousId: string) => void
): void {
  bridge.consumers.delete(consumer);
  if (bridge.consumers.size > 0) return;
  bridge.cleanup();
  if (activeParentIdentityBridge === bridge) activeParentIdentityBridge = null;
}

/**
 * Provides one anonymous player identity for gameplay, analytics, streaks, and signup claiming.
 * Loader-created embeds first resolve the partner page's first-party identity via postMessage.
 */
export const useAnonymousId = (): string | null => {
  const candidateIdRef = useRef<string | null>(null);
  if (candidateIdRef.current === null) {
    if (typeof globalThis.window === 'undefined') {
      candidateIdRef.current = 'ssr-placeholder';
    } else {
      const candidateId = getAnonymousId() ?? uuidv4();
      candidateIdRef.current = candidateId;
    }
  }

  const [anonymousId, setAnonymousId] = useState<string | null>(() =>
    isParentManagedAnonymousIdentity() ? null : candidateIdRef.current
  );
  const storageUnavailableRef = useRef(false);

  useEffect(() => {
    if (typeof globalThis.window === 'undefined') return;

    const currentCandidateId = candidateIdRef.current;
    const candidateId =
      currentCandidateId === null || currentCandidateId === 'ssr-placeholder'
        ? (getAnonymousId() ?? uuidv4())
        : currentCandidateId;
    candidateIdRef.current = candidateId;
    const unsubscribeIdentity = subscribeAnonymousIdentity((nextAnonymousId) => {
      candidateIdRef.current = nextAnonymousId;
      setAnonymousId(nextAnonymousId);
    });

    removeRetiredAnonymousIdentityHandoffParam();

    if (isParentManagedAnonymousIdentity()) {
      const parentOrigin = resolveParentManagedAnonymousIdentityOrigin();
      if (!parentOrigin) {
        rememberAnonymousId(candidateId);
        setAnonymousId(candidateId);
        return unsubscribeIdentity;
      }
      clearAnonymousIdentityResolutionFailure();
      const releaseBridge = acquireParentIdentityBridge(candidateId, parentOrigin, setAnonymousId);
      return () => {
        unsubscribeIdentity();
        releaseBridge();
      };
    }

    clearAnonymousIdentityResolutionFailure();
    const initializeId = () => {
      if (storageUnavailableRef.current) return;
      try {
        const storedId = localStorage.getItem(ANONYMOUS_ID_STORAGE_KEY);
        if (storedId) {
          rememberAnonymousId(storedId);
          return;
        }
        const nextCandidateId = candidateIdRef.current ?? getAnonymousId() ?? uuidv4();
        candidateIdRef.current = nextCandidateId;
        rememberAnonymousId(nextCandidateId);
      } catch {
        storageUnavailableRef.current = true;
        setAnonymousId(getAnonymousId() ?? candidateId);
      }
    };

    const handleStorageChange = (event: StorageEvent) => {
      if (storageUnavailableRef.current || event.key !== ANONYMOUS_ID_STORAGE_KEY) return;
      if (event.newValue) {
        adoptAnonymousIdFromStorage(event.newValue);
        return;
      }
      rememberAnonymousId(uuidv4());
    };

    initializeId();
    const pollInterval = globalThis.window.setInterval(() => {
      if (storageUnavailableRef.current) return;
      try {
        if (!localStorage.getItem(ANONYMOUS_ID_STORAGE_KEY)) initializeId();
      } catch {
        storageUnavailableRef.current = true;
      }
    }, 1000);
    globalThis.window.addEventListener('storage', handleStorageChange);

    return () => {
      unsubscribeIdentity();
      globalThis.window.removeEventListener('storage', handleStorageChange);
      globalThis.window.clearInterval(pollInterval);
    };
  }, []);

  return anonymousId;
};
